/**
 * Tries to JSON.parse() `input` and instead of throw on error, just logs to console.
 * If typeof input was undefined, then logs as warning, otherwise logs as error.
 * @param {string} input
 */
export function safeJsonParse(input: string, logFail = true) {
    const type = typeof input;
    try {
        return JSON.parse(input);
    } catch (err) {
        if (logFail) {
            if (type === 'undefined') {
                console.warn('safeJsonParse() Undefined is not a valid JSON:', err);
            } else {
                console.error('safeJsonParse() Could not parse as JSON:', input, `type: ${type}, error:`, err);
            }
        }
    }
}

/**
 * Tries to copy input array by JSON.parse(JSON.stringify()) and also tries to always return an array.
 * @param {array} input array to copy
 * @return {array}
 */
export function safeArrCopy(input: any[]): any[] {
    if (input === null || typeof input === "undefined")
        return [];

    const type = typeof input;
    if (type === 'undefined') {
        console.warn('safeArrCopy() Undefined is not an array');
        return [];
    }
    if (!Array.isArray(input)) {
        console.error(`safeArrCopy() Array is expected, got: ${typeof input}. Input:`, input);
        return [];
    }
    try {
        return JSON.parse(JSON.stringify(input));
    } catch (err) {
        console.error('safeArrCopy() Could not parse/stringify input:', input, `type: ${ type }, error:`, err);
        return [];
    }
}

/**
 * Tries to copy input object by JSON.parse(JSON.stringify()) and also tries to always return an object.
 * @param {Object} input Object to copy
 * @return {Object}
 */
export function safeObjCopy(input) {
    if (input === null || typeof input === "undefined")
        return {};

    const type = typeof input;
    if (type === 'undefined') {
        console.warn('safeObjCopy() Undefined is not an object');
        return {}
    }
    if (type !== 'object') {
        console.error(`safeObjCopy() Object is expected, got: ${type}. Input:`, input);
        return {};
    }
    try {
        return JSON.parse(JSON.stringify(input));
    } catch (err) {
        console.error('safeObjCopy() Could not parse/stringify input:', input, `type: ${type}, error:`, err);
        return {};
    }
}

/**
 * Recursively compare an object structure
 * Returns the list of differences found
 * @param input Object to compare
 * @param reference Original/reference object.
 */
export function recursiveCompare(input: any, reference: any, path = ""):
    { path: string, diff: string, input: string, reference: string, key: string }[]
{
    let differences = [];
    if (typeof input !== typeof reference) {
        differences.push({ path, diff: 'type', input: typeof input, reference: typeof reference });
        return differences;
    }
    if ((input === null || reference === null) && input != reference) {
        differences.push({ path, diff: 'type', input: input === null ? "null" : input, reference: reference === null ? "null" : reference });
        return differences;
    } else if (input === null && reference === null) {
        return differences;
    }
    if (Array.isArray(input)) {
        //console.log("PATH", path, 'array');
        if (!Array.isArray(reference)) {
            differences.push({ path, diff: 'type' });
            return differences;
        }
        if (input.length != reference.length) {
            differences.push({ path, diff: 'count', input: input.length, reference: reference.length });
        }
        let sortedInput = input.sort(arraySorter);
        let sortedReference = reference.sort(arraySorter);
        for (let key in sortedInput) {
            differences = differences.concat(
                recursiveCompare(sortedInput[key], sortedReference[key], path === '' ? key : path + '.' + key).map(df => { df.key = key; return df; })
            );
        }
    } else if (typeof input === "object") {
        //console.log("PATH", path, 'object');
        for (let key of Object.keys(input)) {
            differences = differences.concat(
                recursiveCompare(input[key], reference[key], path === '' ? key : path + '.' + key).map(df => { df.key = key; return df; })
            );
        }
        for (let key of Object.keys(reference)) {
            if (typeof input[key] === "undefined") 
                differences.push({ path, diff: 'value', input: typeof input, reference });
        }
    } else {
        //console.log("PATH", path, 'other');
        if (input !== reference) {
            differences.push({ path, diff: 'value', input, reference });
        }
    }

    return differences;
}

function arraySorter(a, b) {
    if (typeof a === "object" && typeof b === "object") {
        if (typeof a.UUID != "undefined" && typeof b.UUID != "undefined") {
            return arraySorter(a.UUID, b.UUID);
        }
        return 0;
    }
    
    return a > b ? 1 : (a === b ? 0 : -1); 
}